// MCP Security — Approvals (roadmap item 6). MCP_ROADMAP_UI_2026_11_03
//
// Calls to high-risk tools on endpoints with Human approval on are held at
// the edge. Each held call (same session + tool + arguments) shows up here;
// approving lets the agent's next identical call through once.
//
//   GET  /mcp/sites/:site_uid/approvals
//   POST /mcp/sites/:site_uid/approvals/:id/approve | /deny

import React, { useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Box, Stack, Typography, Alert, Button, LinearProgress } from "@mui/material";

import axios from "../../../../../utils/axios/v1/userAxios";
import V2PageHeader from "../../../../common/V2PageHeader";
import SiteScopeGate from "../../../../common/SiteScopeGate";
import useGlobalSite from "../../../../../hooks/user/useGlobalSite";
import { useNotification } from "../../../../../contexts/NotificationContext";

import tokens from "./_shared/tokens";
import MetricCard from "./_shared/MetricCard";
import useMcpQuery from "./_shared/useMcpQuery";
import { primaryButtonStyle, dangerButtonStyle, BORDER_COLOR } from "./_shared/buttonStyles";

function fmtAgo(sec) {
  if (!sec) return "—";
  const d = Math.max(0, Math.floor(Date.now() / 1000 - Number(sec)));
  if (d < 60) return `${d}s ago`;
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  return `${Math.floor(d / 3600)}h ago`;
}

const STATUS = {
  pending: { bg: "#FFF4D6", fg: "#8A6410" },
  approved: { bg: "#E6F4EC", fg: "#1E6B3F" },
  denied: { bg: "#FBE8E8", fg: "#8E1F1F" },
};

export default function McpApprovals() {
  const { effectiveSiteUid } = useGlobalSite();
  const { showNotification } = useNotification();
  const [busy, setBusy] = useState(null);
  const path = effectiveSiteUid ? `mcp/sites/${effectiveSiteUid}/approvals?limit=200` : null;
  const { data: resp, error, loading, refetch } = useMcpQuery(path, { skip: !path });
  const rows = resp?.data || [];

  const metrics = useMemo(() => [
    { label: "Pending", value: String(rows.filter((r) => r.status === "pending").length), sub: "waiting for a decision" },
    { label: "Approved", value: String(rows.filter((r) => r.status === "approved").length), sub: "last 15 min" },
    { label: "Denied", value: String(rows.filter((r) => r.status === "denied").length), sub: "last 15 min" },
  ], [rows]);

  const decide = async (id, action) => {
    if (!effectiveSiteUid || busy) return;
    setBusy(id);
    try {
      await axios.post(`mcp/sites/${effectiveSiteUid}/approvals/${id}/${action}`, {});
      showNotification({ severity: "success", message: action === "approve" ? "Approved — the agent's next identical call goes through once." : "Denied." });
      refetch && refetch();
    } catch (e) {
      showNotification({ severity: "error", message: e?.response?.data?.message || e.message || `${action} failed` });
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <Helmet title="MCP Security — Approvals" />
      <Stack spacing={3} sx={{ p: 6 }}>
        <V2PageHeader
          title="MCP Security — Approvals"
          description="Calls to destructive (or high-risk) tools are held until someone approves that exact call. Approval is single-use and bound to the session, tool and arguments; requests expire after 15 minutes."
        />
        <SiteScopeGate mode="any">
          {() => (
            <>
              {!effectiveSiteUid && <Alert severity="info">Select a site from the header to see approvals.</Alert>}
              {error && <Alert severity="error">{error}</Alert>}
              {resp?.meta?.partial_regions?.length ? <Alert severity="warning">Some regions didn't answer: {resp.meta.partial_regions.join(", ")}</Alert> : null}
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 1.5 }}>
                {metrics.map((m) => <MetricCard key={m.label} label={m.label} value={m.value} sub={m.sub} />)}
              </Box>
              <Box sx={{ background: "#fff", border: `1px solid ${tokens.neutral[200]}`, borderRadius: "8px", overflow: "hidden" }}>
                {loading && <LinearProgress />}
                <Box sx={{ display: "grid", gridTemplateColumns: "minmax(140px,1fr) 100px minmax(220px,2fr) 140px 90px 90px 190px", gap: "0 12px", px: 2, height: 38, alignItems: "center", fontSize: 12, color: tokens.neutral[700], borderBottom: `1px solid ${BORDER_COLOR}` }}>
                  <span>Tool</span><span>Risk</span><span>Arguments</span><span>Agent</span><span>Requested</span><span>Status</span><span />
                </Box>
                {!rows.length && !loading && (
                  <Box sx={{ p: 3, textAlign: "center", color: tokens.neutral[700], fontSize: 13 }}>
                    No held calls. Turn on Human approval under Endpoint settings → Agent & tool controls, and set tool risk classes on the Tools page.
                  </Box>
                )}
                {rows.map((r) => {
                  const st = STATUS[r.status] || STATUS.pending;
                  return (
                    <Box key={r.approval_id} sx={{ display: "grid", gridTemplateColumns: "minmax(140px,1fr) 100px minmax(220px,2fr) 140px 90px 90px 190px", gap: "0 12px", px: 2, minHeight: 48, py: 1, alignItems: "center", borderBottom: `1px solid ${tokens.neutral[150]}`, fontSize: 13 }}>
                      <span style={{ fontFamily: tokens.fontMono, fontWeight: 500 }}>{r.tool}</span>
                      <span>{r.risk}</span>
                      <span style={{ fontFamily: tokens.fontMono, fontSize: 12, color: tokens.neutral[800], wordBreak: "break-all" }}>{r.args_preview || "—"}</span>
                      <span style={{ fontSize: 12 }}>{r.agent_id || "—"}</span>
                      <span style={{ fontSize: 12, color: tokens.neutral[700] }} title={`${r.attempts} attempt(s) · ${r.region}`}>{fmtAgo(r.created)}</span>
                      <span><span style={{ fontSize: 11, padding: "1px 8px", borderRadius: 999, background: st.bg, color: st.fg }}>{r.status}</span></span>
                      <Stack direction="row" spacing={1}>
                        {r.status === "pending" ? (
                          <>
                            <Button size="small" disabled={busy === r.approval_id} onClick={() => decide(r.approval_id, "approve")} sx={primaryButtonStyle}>Approve</Button>
                            <Button size="small" disabled={busy === r.approval_id} onClick={() => decide(r.approval_id, "deny")} sx={dangerButtonStyle}>Deny</Button>
                          </>
                        ) : (
                          <Typography sx={{ fontSize: 12, color: tokens.neutral[600] }}>{r.decided_by ? `by ${r.decided_by}` : ""}</Typography>
                        )}
                      </Stack>
                    </Box>
                  );
                })}
              </Box>
            </>
          )}
        </SiteScopeGate>
      </Stack>
    </>
  );
}
